import {
  ChangeDetectionStrategy,
  Component,
  computed,
  model,
  signal,
} from '@angular/core';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import {
  ELSI_REMINDERS_HIDE_SMS,
  ElsiReminderPolicy,
  ElsiReminderPolicyOverride,
} from '@et/typings';
import { ElsiReminderEditComponent } from '../elsi-reminder-edit/elsi-reminder-edit.component';

@Component({
  selector: 'et-atoms-elsi-reminder',
  imports: [OverlayModule, CdkTrapFocus, ElsiReminderEditComponent],
  templateUrl: './elsi-reminder.component.html',
  styleUrl: './elsi-reminder.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'inline-flex',
  },
})
export class ElsiReminderComponent {
  policy = model<ElsiReminderPolicy | null>(null);

  isOpen = signal(false);
  mode = signal<'preview' | 'edit'>('preview');

  canOpen = computed(() => this.policy()?.enabled === true);

  protected hideSms = ELSI_REMINDERS_HIDE_SMS;

  readonly positions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  /**
   * Opens the preview when a reminder policy exists and is enabled.
   */
  open(): void {
    if (!this.canOpen()) {
      return;
    }
    this.mode.set('preview');
    this.isOpen.set(true);
  }

  /**
   * Closes the overlay and returns it to the preview.
   */
  close(): void {
    this.mode.set('preview');
    this.isOpen.set(false);
  }

  /**
   * Closes the overlay when Escape is pressed inside it.
   */
  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  /**
   * Shows the override form when the policy allows a sender override.
   */
  showEdit(): void {
    if (!this.policy()?.allowSenderOverride) {
      return;
    }
    this.mode.set('edit');
  }

  /**
   * Stores the sender override and returns to the preview.
   */
  onSaved(override: ElsiReminderPolicyOverride): void {
    const current = this.policy();
    if (!current) {
      return;
    }
    this.policy.set({ ...current, ...override });
    this.mode.set('preview');
  }
}
